import api from 'api/api';

import { SupplyChainNeighborhood, SupplyChainNode } from './types';

// Batch size for every paged path; the +N badge still shows the API total, not this size.
export const MAX_NODES_PER_EXPANSION = 5;

// Both limits are validated, not clamped: 1–20 only, and the bare `limit`/`offset` names are ignored.
export type NeighborhoodPageParams = {
  inputs_offset?: number;
  inputs_limit?: number;
  outputs_offset?: number;
  outputs_limit?: number;
};

export const fetchNeighborhood = async (
  nodeId: string,
  version: string,
  system_model: string,
  page: NeighborhoodPageParams = {}
): Promise<SupplyChainNeighborhood> => {
  const { data } = await api.get(`/web/v4/nodes/${nodeId}`, {
    params: { version, system_model, ...page },
  });
  return data;
};

// FE-only edge derived from neighborhood amounts — not returned by the BE.
export type DerivedEdge = {
  id: string;
  source_id: string;
  target_id: string;
  amount?: number;
  unit: string;
  // The product traded on this edge. Absent only on the root, which is never a row source here, so `''` is unreachable.
  flow_object: string;
  // `is_functional` flags whether this exchange is the queried node's own reference exchange; `child_id` is
  // the neighbour it is attributed to for card styling (note: the same exchange seen from the opposite end
  // can differ, and `mergeEdges` keeps the first — a known, deferred caveat).
  child_id: string;
  is_functional?: boolean;
};

// Optional per direction: a missing count renders an icon-only pill, whereas `0` renders no pill at all.
export type NodeCounts = Record<string, { inputs?: number; outputs?: number }>;
export type ShownCounts = Record<string, { inputs: number; outputs: number }>;

// Seeds the +N badges from each node's own counts, so they are numbered before that node is ever expanded.
export const countsFromNodes = (nodes: SupplyChainNode[]): NodeCounts =>
  nodes.reduce<NodeCounts>((acc, n) => {
    if (n.input_count !== undefined && n.output_count !== undefined) {
      acc[n.id] = { inputs: n.input_count, outputs: n.output_count };
    }
    return acc;
  }, {});

export const edgeId = (sourceId: string, targetId: string) => `${sourceId}->${targetId}`;

// `addedByExpansion` / `edgesByExpansion` keys are `${nodeId}_inputs` / `${nodeId}_outputs`.
export const expansionOwnerId = (key: string) => key.replace(/_inputs$|_outputs$/, '');

export const makeInputEdges = (focalId: string, inputs: SupplyChainNode[]): DerivedEdge[] =>
  inputs
    .filter((n) => n.id !== focalId)
    .map((n) => ({
      id: edgeId(n.id, focalId),
      source_id: n.id,
      target_id: focalId,
      amount: n.amount,
      unit: n.unit,
      flow_object: n.flow_object ?? '',
      child_id: n.id,
      is_functional: n.is_functional,
    }));

export const makeOutputEdges = (focalId: string, outputs: SupplyChainNode[]): DerivedEdge[] =>
  outputs
    .filter((n) => n.id !== focalId)
    .map((n) => ({
      id: edgeId(focalId, n.id),
      source_id: focalId,
      target_id: n.id,
      amount: n.amount,
      unit: n.unit,
      flow_object: n.flow_object ?? '',
      child_id: n.id,
      is_functional: n.is_functional,
    }));

// A card renders functional when ANY edge into it is, so the result cannot depend on expansion order
// and a multi-parent card cannot disagree with its own edge colours.
export const functionalChildIds = (edges: DerivedEdge[]): Set<string> =>
  new Set(edges.filter((e) => e.is_functional).map((e) => e.child_id));

export type ExpansionResolution = {
  // New cards, or composite duplicates of ancestors.
  nodesToAdd: SupplyChainNode[];
  // Every connected card, including ones already on canvas: edges come from here, so nothing is lost.
  instances: SupplyChainNode[];
};

export const resolveExpansionInstances = (
  focalId: string,
  candidates: SupplyChainNode[],
  existingIds: Set<string>,
  targetDepth: number,
  isAncestor: (candidateId: string) => boolean,
  getDepth: (id: string) => number
): ExpansionResolution => {
  const nodesToAdd: SupplyChainNode[] = [];
  const instances: SupplyChainNode[] = [];
  const seen = new Set<string>();

  for (const candidate of candidates) {
    let instance = candidate;
    let isNew = !existingIds.has(candidate.id);

    // Re-adding an ancestor would form a cycle, so it becomes a composite duplicate — unless it lands in the same column.
    if (!isNew && isAncestor(candidate.id) && getDepth(candidate.id) !== targetDepth) {
      const compositeId = `${candidate.id}#${focalId}`;
      instance = { ...candidate, id: compositeId, datasetId: candidate.id };
      isNew = !existingIds.has(compositeId);
    }

    if (instance.id === focalId || seen.has(instance.id)) continue;
    seen.add(instance.id);

    instances.push(instance);
    if (isNew) nodesToAdd.push(instance);
  }

  return { nodesToAdd, instances };
};

export const mergeNodes = (prev: SupplyChainNode[], incoming: SupplyChainNode[]): SupplyChainNode[] => [
  ...prev,
  ...incoming.filter((n) => !prev.find((e) => e.id === n.id)),
];

export const mergeEdges = (prev: DerivedEdge[], incoming: DerivedEdge[]): DerivedEdge[] => [
  ...prev,
  ...incoming.filter((e) => !prev.find((ex) => ex.id === e.id)),
];

// Cascades through the whole addedByExpansion subtree; pre-existing nodes are never in it, so they survive.
export const collectNodesToRemove = (
  directIds: string[],
  addedByExpansion: Record<string, Set<string>>,
  protectedNodes: Set<string>
): Set<string> => {
  const toRemove = new Set<string>();
  const visit = (id: string) => {
    if (toRemove.has(id) || protectedNodes.has(id)) return;
    toRemove.add(id);
    addedByExpansion[`${id}_inputs`]?.forEach((childId) => visit(childId));
    addedByExpansion[`${id}_outputs`]?.forEach((childId) => visit(childId));
  };
  directIds.forEach(visit);
  return toRemove;
};
